// UpgradeModal.jsx - Beautiful upgrade prompt like Claude
import React from 'react';
import './UpgradeModal.css';
import { PLAN_LIMITS } from '../constants/planLimits';

function UpgradeModal({
  isOpen,
  onClose,
  currentPlan = 'free',
  limitType = 'projects', // 'projects'
  usage = {},
  suggestedPlan = null
}) {
  if (!isOpen) return null;

  // Determine which plan to suggest
  const getRecommendedPlan = () => {
    if (suggestedPlan) {
      // Normalize plan name to lowercase to match PLAN_LIMITS keys
      const normalizedPlan = suggestedPlan.toLowerCase();
      // Verify the plan exists in PLAN_LIMITS
      if (PLAN_LIMITS[normalizedPlan]) {
        return normalizedPlan;
      }
    }

    if (currentPlan === 'free') return 'starter';
    if (currentPlan === 'starter') return 'creator';
    if (currentPlan === 'creator') return 'professional';
    if (currentPlan === 'professional') return 'professional'; // Already at top
    return 'starter';
  };

  const recommendedPlan = getRecommendedPlan();
  const currentPlanData = PLAN_LIMITS[currentPlan];
  const nextPlanData = PLAN_LIMITS[recommendedPlan];

  // Safety check - if nextPlanData is undefined, close modal and log error
  if (!nextPlanData) {
    console.error(`❌ Invalid plan recommended: ${recommendedPlan}. Available plans:`, Object.keys(PLAN_LIMITS));
    onClose();
    return null;
  }

  // Generate limit message
  const getLimitMessage = () => {
    return {
      title: `${currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)} Plan Limit Exceeded`,
      description: `You've used all your monthly projects. Upgrade to continue creating.`,
      currentUsage: [
        `Projects: ${usage.prompt || 0}/${currentPlanData.monthlyProjects.prompt} (limit reached)`
      ]
    };
  };

  const limitInfo = getLimitMessage();

  // Get upgrade benefits based on what user needs
  const getUpgradeBenefits = () => {
    const benefits = [];

    benefits.push(`${nextPlanData.monthlyProjects.prompt} projects per month`);
    benefits.push('Unlimited PNG & PPTX exports');

    if (recommendedPlan === 'creator' || recommendedPlan === 'professional') {
      benefits.push('Priority rendering queue');
    }

    if (recommendedPlan === 'professional') {
      benefits.push('Priority support');
    }

    return benefits;
  };

  const benefits = getUpgradeBenefits();

  const handleUpgrade = () => {
    // Navigate to pricing page with pre-selected plan
    window.location.href = `/pricing-plans?plan=${recommendedPlan}`;
  };

  return (
    <div className="upgrade-modal-overlay" onClick={onClose}>
      <div className="upgrade-modal" onClick={(e) => e.stopPropagation()}>
        <button className="upgrade-modal-close" onClick={onClose}>
          ×
        </button>

        <div className="upgrade-modal-header">
          <div className="upgrade-modal-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
            </svg>
          </div>
          <h2>{limitInfo.title}</h2>
          <p className="upgrade-modal-subtitle">{limitInfo.description}</p>
        </div>

        {limitInfo.currentUsage.length > 0 && (
          <div className="upgrade-current-usage">
            <h3>Current Status</h3>
            <ul>
              {limitInfo.currentUsage.map((item, index) => (
                <li key={index}>
                  <span className="usage-bullet">•</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="upgrade-benefits">
          <h3>Upgrade to {nextPlanData.name} and unlock:</h3>
          <ul>
            {benefits.map((benefit, index) => (
              <li key={index}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                {benefit}
              </li>
            ))}
          </ul>
        </div>

        <div className="upgrade-pricing">
          <span className="upgrade-price">${nextPlanData.price.toFixed(2)}</span>
          <span className="upgrade-period">/month</span>
        </div>

        <div className="upgrade-modal-actions">
          <button className="upgrade-btn-secondary" onClick={onClose}>
            Maybe Later
          </button>
          <button className="upgrade-btn-primary" onClick={handleUpgrade}>
            Upgrade to {nextPlanData.name}
          </button>
        </div>

        <p className="upgrade-modal-footer">
          Cancel anytime. No hidden fees.
        </p>
      </div>
    </div>
  );
}

export default UpgradeModal;